<!-- Advisor Landing Page with no Ongoing Conversation -->
<div class="advisor-container" *ngIf="(messages$ | async)?.length === 0; else chatDisplay">
  <h1>Advisor</h1>
  <div class="options">
    <button mat-button class="history" (click)="openHistory()" data-test-id="history-button">
      <mat-icon>history</mat-icon>
      <span>History</span>
    </button>
    <div class="vertical-line"></div>
    <!-- Select Model -->
    <button mat-button [matMenuTriggerFor]="modelSelect" data-test-id="llm-model-dropdown">
      {{ selectedModel?.name || 'Select a model' }}
      <mat-icon>arrow_drop_down</mat-icon>
    </button>

    <mat-menu #modelSelect="matMenu">
      <button mat-menu-item *ngFor="let model of models.modelList" (click)="selectModel(model)">
        {{ model.name }}
      </button>
    </mat-menu>
    <div class="vertical-line"></div>

    <!-- Select Collection with Autocomplete -->
    <button
      mat-button
      [matMenuTriggerFor]="collectionSelect"
      #collectionMenuTrigger="matMenuTrigger"
      (menuOpened)="focusCollectionInput()"
      data-test-id="collections-dropdown">
      {{ collectionButtonLabel }}
      <mat-icon>arrow_drop_down</mat-icon>
    </button>

    <mat-menu
      #collectionSelect="matMenu"
      [hasBackdrop]="true"
      class="collection-select-menu"
      (opened)="onCollectionMenuOpened()"
      (closed)="applySelectedCollections(); autoTrigger?.closePanel()"
    >
      <div
        tabindex="0"
        (click)="$event.stopPropagation()"
        (keydown.space)="$event.stopPropagation()"
        (keydown.enter)="$event.stopPropagation()">
        <mat-form-field appearance="fill" class="collection-search-field">
          <mat-chip-grid #chipGrid aria-label="Selected collections">
            <mat-chip-row
              *ngFor="let c of selectedCollectionsArray; trackBy: trackByCollection"
              (removed)="onChipRemoved(c, autoTrigger)"
              [removable]="true">
              {{ c.name }}
              <button
                matChipRemove
                (mousedown)="$event.preventDefault()"
                (click)="$event.stopPropagation()"
                [attr.aria-label]="'Remove ' + c.name">
                <mat-icon>cancel</mat-icon>
              </button>
            </mat-chip-row>
          </mat-chip-grid>
          <input
            type="text"
            matInput
            [formControl]="collectionSearchControl"
            [matChipInputFor]="chipGrid"
            [matAutocomplete]="auto"
            [placeholder]="collectionPlaceHolder"
            autocomplete="off"
            (blur)="handleInputBlur($event)"
            #collectionInput
            #autoTrigger="matAutocompleteTrigger"
          />
          <mat-autocomplete
            #auto="matAutocomplete"
            [displayWith]="displayCollectionFn"
            panelWidth="300px">
            <mat-option
              *ngFor="let collection of filteredCollections | async"
              class="collection-option"
              (mousedown)="$event.preventDefault()"
              (click)="$event.stopPropagation()"
              >
              <mat-checkbox
                [checked]="isSelected(collection)"
                (click)="$event.stopPropagation()"
                (change)="onToggleCollection(collection, $event, autoTrigger)"
                >
                {{ collection.name }}
              </mat-checkbox>
            </mat-option>
          </mat-autocomplete>
        </mat-form-field>
      </div>
    </mat-menu>
    <div class="vertical-line"></div>

    <button mat-button (click)="openFilterModal()" [disabled]="!hasFilterOptions" data-test-id="filters-button">
      <mat-icon>list</mat-icon>
      <span class="spacer"></span>
      Filters ({{ filterCount }})
    </button>
  </div>

  <!-- Polling Status Indicator for initial page -->
  <app-polling-status-indicator
    [pollingStatus$]="pollingStatus$">
  </app-polling-status-indicator>

  <app-prompt-box
    class="prompt-box"
    [(prompt)]="prompt"
    [label]="'Ask specific questions to the LLM with contextual answers from your document'"
    (sendMessageEvent)="sendMessage()"
    (clearSessionEvent)="clearSession()"
    [disabled]="(isTyping$ | async) ?? false"
    data-test-id="advisor-prompt">
  </app-prompt-box>
</div>

<!-- Ongoing Conversation -->
<ng-template #chatDisplay>
  <div class="advisor-container advisor-container--chat-mode">
    <!-- Chat content -->
    <mat-sidenav-container class="chat-content-pane">
      <div class="advisor-container advisor-container--chat-mode">
        <div class="options options--chat-mode">
          <button mat-button class="history" (click)="openHistory()" data-test-id="history-button">
            <mat-icon>history</mat-icon>
            <span>History</span>
          </button>
          <div class="vertical-line"></div>
          <button mat-button (click)="clearSession()">
            <mat-icon>chat</mat-icon>
            <span>New Discussion</span>
          </button>
          <div class="vertical-line"></div>
          <!-- Select Model -->
          <button mat-button disabled data-test-id="llm-model-dropdown">
            {{ selectedModel?.name || 'Select a model' }}
            <mat-icon>arrow_drop_down</mat-icon>
          </button>

          <div class="vertical-line"></div>

          <!-- Select Collection -->
          <button mat-button disabled data-test-id="collections-dropdown">
            {{ collectionButtonLabel }}
            <mat-icon>arrow_drop_down</mat-icon>
          </button>

          <div class="vertical-line"></div>

          <button mat-button (click)="openFilterModal()" disabled data-test-id="filters-button">
            <mat-icon>list</mat-icon>
            <span class="spacer"></span>
            Filters ({{ filterCount }})
          </button>
        </div>
        <mat-card-content class="preview-content">
          <app-message-list
            [messages]="(messages$ | async) ?? []"
            [loading]="(loading$ | async) ?? false"
            [isTyping]="(isTyping$ | async) ?? false"
            [currentTypingMessage]="currentTypingMessage$ | async"
            [sourceDocuments]="(sourceDocuments$ | async) ?? []"
            (draftSectionEvent)="draftSection($event)"
            (snackbarEvent)="showSnackbar($event)"
            (citationClicked)="onCitationClick($event)">
          </app-message-list>

          <!-- Polling Status Indicator -->
          <app-polling-status-indicator
            [pollingStatus$]="pollingStatus$">
          </app-polling-status-indicator>
        </mat-card-content>
        <app-prompt-box
          [disabled]="(loading$ | async) ?? false"
          class="prompt-box prompt-box--chat-mode"
          [(prompt)]="prompt"
          [label]="'Ask specific questions to the LLM with contextual answers from your document'"
          (sendMessageEvent)="sendMessage()"
          (clearSessionEvent)="clearSession()"
          data-test-id="advisor-prompt">
        </app-prompt-box>

        <mat-menu #modelMenu="matMenu" xPosition="before" overlapTrigger="true" class="custom-menu">
          <button
            mat-menu-item
            *ngFor="let model of models.modelList"
            [disabled]="model === selectedModel"
            (click)="selectModel(model)"
            class="model-options">
            {{ model.name }}
          </button>
        </mat-menu>
      </div>
    </mat-sidenav-container>
    <!-- Source Documents Pane -->
    <mat-sidenav
      #rightSidenav
      position="end"
      [mode]="'side'"
      [opened]="true"
      class="side-sidebar"
      data-test-id="source-documents-pane">
      <div class="source-documents-pane">
        <p class="default-cursor">
          Source Information
          <mat-icon
            matTooltip="This section lists the context used from the collection of documents relevant to your prompt. Higher scores indicate higher relevance."
            aria-label="Information about source documents"
            class="info-icon">
            info
          </mat-icon>
        </p>
        <!-- Source cards -->
        <div *ngIf="sourceDocuments$ | async as sourceDocuments; else noSourceDocuments">
          <div *ngIf="sourceDocuments.length > 0; else noSourceDocuments">
            <div *ngFor="let doc of sourceDocuments" class="source-card-wrapper">
              <div  class="source-information-card-container">
                <div class="citation-number-badge">
                  <span class="citation-number-text">[{{doc.citation_id}}]</span>
                </div>
                <div class="source-information-card">
                  <div class="source-information-card source-information-card--header">
                    <p>Relevance Score: {{ convertDecimalToPercent(doc.score) }}</p>
                    <button mat-icon-button (click)="openSourceDocumentDialog(doc)" aria-label="See source content">
                      <mat-icon class="material-symbols-outlined">expand_content</mat-icon>
                    </button>
                  </div>
                  <div class="source-information-card source-information-card--body">
                    <p>{{ doc.metadata.title || 'No metadata provided' }}</p>
                  </div>
                </div>
              </div>
              <mat-divider></mat-divider>
            </div>
          </div>
        </div>

        <!-- No Sources Available -->
        <ng-template #noSourceDocuments>
          <p>No source documents available.</p>
        </ng-template>
      </div>
    </mat-sidenav>
  </div>
</ng-template>
